import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { PaymentContext } from '../../context/PaymentContext';
import { track, EVENTS } from '../../services/analytics';
import { trialEndDate, TRIAL_TERMS } from '../../content/trial';
import { isGuestPath, isOnboardingPath } from '../../utils/onboardingLanding';

const DISMISS_KEY = 'trialStripDismissed';

/**
 * The 7-day trial's strip (#4703, mock round variant C): one quiet line at
 * the top of every page during the trial, its real end date and the Season
 * Pass link. A date, never a countdown; dismissible for the session. The
 * terms are stated once, in full, on the trial note where the trial starts
 * (the list read). Off first-run journeys and outside an active trial.
 */
export default function TrialStrip() {
    const payment = useContext(PaymentContext);
    const { pathname } = useLocation();
    const [dismissed, setDismissed] = useState(() => {
        try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
    });
    const trial = payment?.isTrial ? payment.trial : null;
    const show = Boolean(trial) && !dismissed && !isOnboardingPath(pathname) && !isGuestPath(pathname);
    // The terms, stated once: the first time this trial's strip shows (an
    // account whose trial began at sign-in never sees the list read's note).
    const termsKey = trial ? `trial_terms_seen_${trial.started_at}` : null;
    const [firstView] = useState(() => {
        try { return termsKey ? !localStorage.getItem(termsKey) : false; } catch { return false; }
    });
    const sent = useRef(false);
    useEffect(() => {
        if (show && !sent.current) {
            sent.current = true;
            track(EVENTS.TRIAL_NOTE_SHOWN, { surface: 'strip' });
            try { if (termsKey) localStorage.setItem(termsKey, '1'); } catch { /* private mode */ }
        }
    }, [show, termsKey]);
    if (!show) return null;
    const end = trialEndDate(trial);
    const dismiss = () => {
        try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* private mode */ }
        setDismissed(true);
    };
    return (
        <div className="flex items-center justify-center gap-2 border-b border-[#D6E8D5] bg-[#F0F7F1] px-4 py-2 text-sm"
            data-testid="trial-strip" role="note">
            <SparklesIcon className="h-4 w-4 shrink-0 text-[#1A4D2E]" aria-hidden="true" />
            <span className="text-[#1A4D2E]">
                {end ? <>Everything in Stratia through <strong>{end}</strong>, no card.</> : <>Everything in Stratia for 7 days, no card.</>}
                {firstView && <span className="ml-1 text-[#4A4A4A]" data-testid="trial-strip-terms">{TRIAL_TERMS}</span>}
            </span>
            <Link to="/pricing?from=trial_strip" data-testid="trial-strip-link"
                onClick={() => track(EVENTS.UPGRADE_CTA_CLICKED, { surface: 'trial_strip' })}
                className="whitespace-nowrap font-semibold text-[#1A4D2E] underline underline-offset-2">
                See the Season Pass
            </Link>
            <button type="button" onClick={dismiss} aria-label="Hide for now"
                className="ml-1 inline-flex h-8 w-8 items-center justify-center rounded hover:bg-white/60">
                <XMarkIcon className="h-4 w-4 text-[#1A4D2E]" />
            </button>
        </div>
    );
}
